import React, {
  useCallback,
  useEffect,
  useState
} from "react";

import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Loader from "../../components/Loader";

import authService from "../../services/authService";
import settingsService from "../../services/settingsService";

const PlayerDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(
    async (silent = false) => {
      try {
        if (!silent) {
          setLoading(true);
        }

        const res =
          await settingsService.getDashboard();

        if (res?.success) {
          setDashData(
            res.dashboard || {}
          );
        }
      } catch (error) {
        if (!silent) {
          toast.error(
            error.response?.data?.message ||
            "Failed to load Player dashboard."
          );
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    setUser(
      authService.getCurrentUser()
    );

    fetchDashboardData();
  }, [fetchDashboardData]);

  const toggleSidebar = () =>
    setSidebarOpen(
      (previous) => !previous
    );

  const closeSidebar = () =>
    setSidebarOpen(false);

  return (
    <div className="d-flex flex-column min-vh-100">
      <Navbar toggleSidebar={toggleSidebar} />

      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar
          isOpen={sidebarOpen}
          closeMobileSidebar={closeSidebar}
        />

        <main className="flex-grow-1 p-3 p-lg-4 overflow-auto">
          <div className="container-fluid">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-4">
              <div>
                <h2 className="fw-bold text-dark mb-1">
                  Player Gaming Dashboard
                </h2>

                <p className="text-secondary mb-0">
                  Welcome back,{" "}
                  <span className="fw-semibold text-primary">
                    {user?.full_name ||
                      user?.username}
                  </span>
                  !
                </p>
              </div>

              <button
                type="button"
                className="btn btn-light border btn-sm mt-3 mt-sm-0 align-self-start align-self-sm-center px-3"
                onClick={() =>
                  fetchDashboardData()
                }
              >
                <i className="bi bi-arrow-clockwise me-1" />
                Refresh Data
              </button>
            </div>

            {loading ? (
              <Loader message="Loading Player dashboard..." />
            ) : (
              <>
                {/* Hero Banner */}
                <div className="admin-hero dealer-hero mb-4">
                  <div>
                    <span className="admin-eyebrow">
                      PLAYER GAMING DESK
                    </span>

                    <h3 className="mb-2">
                      Ready to place your next win?
                    </h3>

                    <p className="mb-0">
                      Launch live casino games, check your real-time wallet coins, and track detailed betting history.
                    </p>
                  </div>

                  <div className="admin-hero-actions">
                    <Link
                      to="/game"
                      className="btn btn-warning"
                    >
                      <i className="bi bi-controller me-2" />
                      Play Game Now
                    </Link>

                    <Link
                      to="/bet-history"
                      className="btn btn-light"
                    >
                      <i className="bi bi-journal-text me-2" />
                      Bet History
                    </Link>
                  </div>
                </div>

                {/* Stat Cards */}
                <div className="row g-3 mb-4">
                  {[
                    [
                      "My Wallet Balance",
                      `🪙 ${Number(
                        dashData?.wallet ??
                          user?.wallet ??
                          0
                      ).toLocaleString(
                        "en-IN"
                      )}`,
                      "bi-wallet2",
                      "tone-success"
                    ],
                    [
                      "Total Bets Played",
                      dashData?.total_bets ??
                        0,
                      "bi-controller",
                      "tone-primary"
                    ],
                    [
                      "Account Status",
                      "ACTIVE",
                      "bi-shield-check",
                      "tone-info"
                    ]
                  ].map(
                    ([
                      label,
                      value,
                      icon,
                      tone
                    ]) => (
                      <div
                        className="col-12 col-sm-6 col-xl-4"
                        key={label}
                      >
                        <div className="admin-stat-card dealer-stat-card h-100">
                          <span
                            className={`admin-stat-icon ${tone}`}
                          >
                            <i
                              className={`bi ${icon}`}
                            />
                          </span>

                          <div>
                            <div className="admin-stat-label">
                              {label}
                            </div>

                            <div className="admin-stat-value dealer-stat-value">
                              {value}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>

                {/* Main Content Panels */}
                <div className="row g-3">
                  {/* Action Cards Panel */}
                  <div className="col-12 col-xl-8">
                    <div className="admin-panel h-100">
                      <div className="admin-panel-head">
                        <div>
                          <h5 className="mb-1 fw-bold text-dark">
                            Player Quick Access
                          </h5>

                          <p className="mb-0 text-muted small">
                            Instant navigation to your core gaming & accounting features.
                          </p>
                        </div>

                        <span className="admin-panel-icon primary">
                          <i className="bi bi-grid-fill" />
                        </span>
                      </div>

                      <div className="dealer-action-grid">
                        <Link
                          to="/game"
                          className="dealer-action-card"
                        >
                          <i className="bi bi-controller" />

                          <div>
                            <strong>
                              Play Casino Game
                            </strong>

                            <span>
                              Enter the game lobby and start playing.
                            </span>
                          </div>

                          <i className="bi bi-chevron-right" />
                        </Link>

                        <Link
                          to="/bet-history"
                          className="dealer-action-card"
                        >
                          <i className="bi bi-journal-text" />

                          <div>
                            <strong>
                              My Bet History
                            </strong>

                            <span>
                              Review all past game bets & results.
                            </span>
                          </div>

                          <i className="bi bi-chevron-right" />
                        </Link>

                        <Link
                          to="/reports"
                          className="dealer-action-card"
                        >
                          <i className="bi bi-file-earmark-bar-graph" />

                          <div>
                            <strong>
                              Account Reports
                            </strong>

                            <span>
                              Track transaction logs and turnover summaries.
                            </span>
                          </div>

                          <i className="bi bi-chevron-right" />
                        </Link>

                        <div className="dealer-action-card border-0 bg-transparent">
                          <i className="bi bi-shield-lock text-gold" />

                          <div>
                            <strong>
                              Secure Session
                            </strong>

                            <span>
                              Your account is verified & active.
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Account Summary Panel */}
                  <div className="col-12 col-xl-4">
                    <div className="admin-panel h-100">
                      <div className="admin-panel-head">
                        <div>
                          <h5 className="mb-1 fw-bold text-dark">
                            Account Snapshot
                          </h5>

                          <p className="mb-0 text-muted small">
                            Player profile & current status.
                          </p>
                        </div>

                        <span className="admin-panel-icon warning">
                          <i className="bi bi-person-badge" />
                        </span>
                      </div>

                      <div className="p-3">
                        <div className="admin-snapshot-row">
                          <span>Player Name:</span>
                          <strong>{user?.full_name || user?.username || "N/A"}</strong>
                        </div>

                        <div className="admin-snapshot-row">
                          <span>Username:</span>
                          <strong>@{user?.username || "player"}</strong>
                        </div>

                        <div className="admin-snapshot-row">
                          <span>Role:</span>
                          <span className="badge bg-warning text-dark fw-bold">PLAYER</span>
                        </div>

                        <div className="admin-snapshot-row">
                          <span>Account Status:</span>
                          <span className="badge bg-success">ACTIVE</span>
                        </div>

                        <div className="admin-snapshot-row">
                          <span>Available Coins:</span>
                          <strong className="text-gold fw-bold">
                            🪙 {Number(dashData?.wallet ?? user?.wallet ?? 0).toLocaleString("en-IN")}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PlayerDashboard;
